/**
 * Session expirée : quand la session d'un utilisateur connecté meurt en cours
 * de route (refresh token refusé, trop d'échecs de refresh, ou backend qui
 * refuse un jeton pourtant frais), AuthContext l'envoie sur la page 401
 * SESSION_EXPIRED_PATH au lieu de le renvoyer en silence sur /login.
 *
 * La page où il était est gardée ici (sessionStorage, donc par onglet et
 * jamais dans l'URL) pour que la reconnexion l'y ramène.
 */
export const SESSION_EXPIRED_PATH = '/session-expiree';

const RETURN_PATH_KEY = 'kaiylo.sessionExpiredFrom';

// Les espaces qui demandent une session. Ailleurs (landing, pages d'auth), une
// session morte ne mérite pas une page d'erreur : le visiteur n'était pas
// en train d'utiliser l'app.
const PROTECTED_PREFIXES = ['/coach', '/student', '/chat', '/onboarding'];

export const isProtectedPath = (pathname) =>
  PROTECTED_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

// Espace de chaque rôle, pour ne pas renvoyer un élève sur la page d'un coach
// (ou l'inverse) si ce n'est pas le même compte qui se reconnecte.
const ROLE_PREFIXES = {
  coach: ['/coach', '/chat'],
  admin: ['/coach', '/chat'],
  student: ['/student', '/chat'],
};

// Session tout juste expirée, en attente de la page 401. Le routeur applique
// la navigation vers la page 401 en différé (transition), alors que
// `setUser(null)` passe tout de suite : la route protégée d'avant voit donc
// d'abord un utilisateur absent et redirigerait vers /login par-dessus. Elle
// lit ce drapeau pour viser la page 401 à la place.
let sessionExpiring = false;

export const isSessionExpiring = () => sessionExpiring;

/** Remis à zéro quand la page 401 s'affiche, ou à la reconnexion. */
export const clearSessionExpiring = () => {
  sessionExpiring = false;
};

/**
 * `userId` : le compte dont la session a expiré. Un autre compte qui se
 * reconnecte dans cet onglet n'est pas renvoyé sur la page du premier.
 */
export const rememberSessionReturnPath = (path, userId = null) => {
  sessionExpiring = true;
  try {
    sessionStorage.setItem(RETURN_PATH_KEY, JSON.stringify({ path, userId }));
  } catch {
    // Stockage indisponible (navigation privée…) : la reconnexion ira au tableau de bord.
  }
};

export const clearSessionReturnPath = () => {
  try {
    sessionStorage.removeItem(RETURN_PATH_KEY);
  } catch {
    // Rien à nettoyer.
  }
};

/** La page à rouvrir après reconnexion pour ce rôle (et ce compte), puis on l'oublie. */
export const consumeSessionReturnPath = (role, userId = null) => {
  let stored = null;
  try {
    stored = sessionStorage.getItem(RETURN_PATH_KEY);
  } catch {
    return null;
  }
  clearSessionReturnPath();
  sessionExpiring = false;
  if (!stored) return null;
  let path = stored;
  let savedUserId = null;
  try {
    const parsed = JSON.parse(stored);
    if (parsed && typeof parsed === 'object') {
      path = parsed.path;
      savedUserId = parsed.userId || null;
    }
  } catch {
    // Ancienne forme : le chemin seul.
  }
  if (typeof path !== 'string' || !path) return null;
  if (savedUserId && userId && savedUserId !== userId) return null;
  const pathname = path.split(/[?#]/)[0];
  const allowed = ROLE_PREFIXES[role] || [];
  return allowed.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)) ? path : null;
};
